import { useEffect, useState } from 'react'
import type { PermissionRequest } from '../../store/types'
import './CountdownTimer.css'

/// Ticks down from the request's timeout. Renders flashing red when under
/// 60s. Calls onExpire once at zero so the overlay can auto-dismiss (the
/// server side will also time out and emit history_entry, but dismissing
/// locally avoids a stale card lingering until the next WS message).
export function CountdownTimer({
  request,
  onExpire,
}: {
  request: PermissionRequest
  onExpire?: () => void
}) {
  const [secondsLeft, setSecondsLeft] = useState(() =>
    computeSecondsLeft(request),
  )

  useEffect(() => {
    if (request.timeout == null) return
    const id = setInterval(() => {
      const next = computeSecondsLeft(request)
      setSecondsLeft(next)
      if (next <= 0) {
        clearInterval(id)
        onExpire?.()
      }
    }, 1000)
    return () => clearInterval(id)
    // Intentional: onExpire may change identity across renders; only
    // restart the interval when the request itself changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request.id, request.timeout])

  if (request.timeout == null) return null

  const urgent = secondsLeft < 60
  const m = Math.max(0, Math.floor(secondsLeft / 60))
  const s = Math.max(0, secondsLeft % 60)

  return (
    <div className={`countdown${urgent ? ' countdown--urgent' : ''}`}>
      <span className="countdown__icon" aria-hidden>
        ⏳
      </span>
      <span className="countdown__time">
        {m}:{s.toString().padStart(2, '0')}
      </span>
    </div>
  )
}

function computeSecondsLeft(request: PermissionRequest): number {
  if (request.timeout == null) return 0
  const elapsed = Math.floor((Date.now() - request.received_at) / 1000)
  return Math.max(0, request.timeout - elapsed)
}
